<template>
  <div>
    <NotificationsContainer />
    <div :class="[alertStore.message ? 'top-[4.5rem]' : 'top-4', 'fixed right-8 z-60 flex space-x-2']">
      <SimpleBadge v-if="showLayoutIndicator && usingNewLayout" :theme="usingNewLayout ? 'green' : 'gray'">
        {{ usingNewLayout ? "New UI" : "Legacy UI" }}
      </SimpleBadge>
      <ChangeAccountButton v-if="showChangeAccount" @click="openSelectAccount" />
      <span v-if="showAccountCode" class="text-sm text-gray-100">{{ session.account.code }}</span>
    </div>
    <ChangeAccountModal v-if="showChangeAccount" v-model:open="selectAccountIsOpen" />
    <PermissionDeniedModal
      :open="alertStore.permissionDenied"
      :permissions="alertStore.permissionsNeeded"
      @close="alertStore.closePermissionDenied"
    />
    <AccountFormModal />
  </div>
</template>
<script lang="ts" setup>
import { ref, onMounted } from "vue";

// Components
import AccountFormModal from "@organisms/AccountFormModal/AccountFormModal.vue";
import NotificationsContainer from "@molecules/NotificationsContainer.vue";
import ChangeAccountButton from "@organisms/ChangeAccount/ChangeAccountButton.vue";
import ChangeAccountModal from "@organisms/ChangeAccount/ChangeAccountModal.vue";
import PermissionDeniedModal from "@molecules/PermissionDeniedModal.vue";
import SimpleBadge from "@atoms/SimpleBadge";

// Application
import { useLocalStorageApp } from "@application";

// Store
import { useTrackingEvents } from "@/vue/composables/trackingevents";
import { storeToRefs } from "pinia";
import { useUserConfigStore, useSessionStore, useAlertStore, useFeaturesStore } from "@store";

const userConfigStore = useUserConfigStore();
const alertStore = useAlertStore();
const sessionStore = useSessionStore();
const featuresStore = useFeaturesStore();
const trackingEventsService = useTrackingEvents();
const localStorageApp = useLocalStorageApp();

const { session } = storeToRefs(sessionStore);
const usingNewLayout = ref(false);
const showLayoutIndicator = ref(false);
const showChangeAccount = ref(false);
const showAccountCode = ref(false);

const selectAccountIsOpen = ref(false);
const openSelectAccount = () => {
  selectAccountIsOpen.value = true;
};

onMounted(async () => {
  if (["", "*"].includes(session.value?.account?.code)) return;

  await Promise.all([userConfigStore.getUserConfig(), featuresStore.fetchFeatures()]);

  const accountsLoggedIn = localStorageApp.getGlobal<string[]>({ id: "ACCOUNTS_LOGGED_IN" }) ?? [];

  await userConfigStore.getUserConfig();
  await featuresStore.fetchFeatures();
  const features = featuresStore.getFeatures();

  usingNewLayout.value = features?.USE_NEW_LAYOUT === "1";
  showLayoutIndicator.value = session?.value?.isMasterUser;

  if (usingNewLayout.value) {
    trackingEventsService.dispatchAll({
      name: "APP_NEW_UI_LOADED",
    });
  }

  showChangeAccount.value =
    Object.entries(session?.value?.acl).length > 1 ||
    (session?.value?.isMasterUser && session?.value?.account?.code !== "") ||
    false;

  showAccountCode.value = !showChangeAccount.value && accountsLoggedIn.length >= 2;
});
</script>
